/**
 * SubscriptionCalendar — [T114-F9-l]
 *
 * A month-grid calendar showing upcoming subscription renewal dates.
 * Each date cell renders subscription emojis/names. Clicking a subscription
 * opens the detail sheet.
 */
/**
 * SubscriptionCalendar — [T114-F9-l]
 *
 * A month-grid calendar showing upcoming subscription renewal dates.
 * Each date cell renders subscription emojis/names. Clicking a subscription
 * opens the detail sheet.
 *
 * Self-managed: dispatches fetchSubscriptionCalendar when the displayed month
 * changes. Reads calendarLoading and calendarTotalMonthly from Redux.
 * Ref: https://redux-toolkit.js.org/api/createAsyncThunk
 */
import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { IconButton } from '@/components/ui/icon-button';
import type { SubscriptionCalendarDay } from '@/lib/types';
import { useCurrency } from '@/hooks/useCurrency';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { fetchSubscriptionCalendar } from '@/features/subscriptions/subscriptionsSlice';

interface SubscriptionCalendarProps {
  onSubscriptionClick: (subscriptionId: string) => void;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function SubscriptionCalendar({
  onSubscriptionClick,
}: SubscriptionCalendarProps) {
  const dispatch = useAppDispatch();
  const { fmt } = useCurrency();
  const calendarDays = useAppSelector((state) => state.subscriptions.calendar);
  const calendarLoading = useAppSelector((state) => state.subscriptions.calendarLoading);
  const calendarTotalMonthly = useAppSelector((state) => state.subscriptions.calendarTotalMonthly);

  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  // Fetch calendar data whenever the displayed month changes
  useEffect(() => {
    const month = format(currentMonth, 'yyyy-MM');
    dispatch(fetchSubscriptionCalendar({ month }));
  }, [dispatch, currentMonth]);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const goToPrevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const goToNextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));
  const goToToday = () => {
    const now = new Date();
    setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  // Build a lookup: dateString (YYYY-MM-DD) -> subscriptions[]
  // The backend now returns ALL occurrences for the month (including weekly/biweekly repeats),
  // so no supplementation from the full subscriptions list is needed.
  const dayLookup = useMemo(() => {
    const map = new Map<string, SubscriptionCalendarDay['subscriptions']>();
    for (const day of calendarDays) {
      const key = day.date.slice(0, 10);
      map.set(key, day.subscriptions);
    }
    return map;
  }, [calendarDays]);

  // Build the month grid
  const calendarGrid = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const grid: Array<{ day: number | null; dateKey: string | null }> = [];

    // Leading blanks
    for (let i = 0; i < firstDay; i++) {
      grid.push({ day: null, dateKey: null });
    }
    // Actual days
    for (let d = 1; d <= daysInMonth; d++) {
      const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      grid.push({ day: d, dateKey });
    }
    return grid;
  }, [year, month]);

  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const monthLabel = currentMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  return (
    <Card data-testid="subscription-calendar">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm font-semibold">Billing Calendar</CardTitle>
            <span
              className="text-xs text-muted-foreground"
              data-testid="calendar-month-total"
              aria-label={`Total subscriptions due in ${format(currentMonth, 'MMMM yyyy')}: ${fmt(calendarTotalMonthly)}`}
            >
              {calendarTotalMonthly > 0 ? fmt(calendarTotalMonthly) : '–'} this month
            </span>
          </div>
          <div className="flex items-center gap-1">
            <IconButton variant="outline" size="sm" tooltip="Previous month" onClick={goToPrevMonth}>
              <ChevronLeft className="h-4 w-4" />
            </IconButton>
            <Button variant="ghost" size="sm" onClick={goToToday} className="min-h-[36px] text-sm font-medium">
              {monthLabel}
            </Button>
            <IconButton variant="outline" size="sm" tooltip="Next month" onClick={goToNextMonth}>
              <ChevronRight className="h-4 w-4" />
            </IconButton>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Desktop grid */}
        <div className="hidden md:block">
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAY_NAMES.map((d) => (
              <div key={d} className="text-center text-xs font-medium text-muted-foreground py-1">
                {d}
              </div>
            ))}
          </div>
          {/* Date cells */}
          {calendarLoading ? (
            <div className="grid grid-cols-7 gap-1" data-testid="calendar-skeleton-grid" aria-busy="true">
              {[...Array(35)].map((_, i) => (
                <Skeleton key={i} className="h-[72px] rounded-md" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid" aria-busy="false">
              {calendarGrid.map((cell, idx) => {
                const subs = cell.dateKey ? dayLookup.get(cell.dateKey) : undefined;
                const isToday = cell.dateKey === todayKey;
                return (
                  <div
                    key={idx}
                    className={cn(
                      'min-h-[72px] border rounded-md p-1 text-xs transition-colors',
                      cell.day === null && 'bg-transparent border-transparent',
                      cell.day !== null && 'bg-card hover:bg-muted/50',
                      isToday && 'border-primary ring-1 ring-primary/20',
                    )}
                  >
                    {cell.day !== null && (
                      <>
                        <div className={cn('font-medium mb-0.5', isToday && 'text-primary')}>
                          {cell.day}
                        </div>
                        {subs && subs.length > 0 && (
                          <div className="space-y-0.5">
                            {subs.slice(0, 3).map((sub) => (
                              <button
                                key={sub.id}
                                type="button"
                                data-testid={`calendar-sub-${sub.id}`}
                                className="w-full text-left text-[10px] leading-tight truncate rounded px-0.5 py-0.5 hover:bg-primary/10 transition-colors cursor-pointer min-h-[22px]"
                                onClick={() => onSubscriptionClick(sub.id)}
                              >
                                {sub.emoji && <span className="mr-0.5">{sub.emoji}</span>}
                                <span>{sub.name}</span>
                                <span className="ml-1 text-muted-foreground">{fmt(sub.amount)}</span>
                              </button>
                            ))}
                            {subs.length > 3 && (
                              <Badge variant="secondary" className="text-[9px] px-1 py-0">
                                +{subs.length - 3} more
                              </Badge>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-2">
          {calendarLoading ? (
            [...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)
          ) : (
            (calendarGrid
              .filter((cell) => {
                if (cell.dateKey === null) return false;
                const subs = dayLookup.get(cell.dateKey);
                return subs && subs.length > 0;
              })
              .map((cell) => {
                const subs = dayLookup.get(cell.dateKey!)!;
                const dateObj = new Date(cell.dateKey! + 'T00:00:00');
                return (
                  <div key={cell.dateKey} className="rounded-lg border p-3 bg-card">
                    <div className="font-medium text-sm mb-2">
                      {dateObj.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                    </div>
                    <div className="space-y-1.5">
                      {subs.map((sub) => (
                        <button
                          key={sub.id}
                          type="button"
                          className="w-full flex items-center gap-2 text-sm p-2 rounded-md hover:bg-muted/50 transition-colors min-h-[44px] text-left"
                          onClick={() => onSubscriptionClick(sub.id)}
                        >
                          <span className="text-lg flex-shrink-0">{sub.emoji || sub.name.charAt(0)}</span>
                          <span className="flex-1 min-w-0 break-words">{sub.name}</span>
                          <span className="text-sm font-semibold flex-shrink-0">{fmt(sub.amount)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }))
          )}
          {!calendarLoading && calendarGrid.every((cell) => !cell.dateKey || !dayLookup.get(cell.dateKey)?.length) && (
            <p className="text-center text-sm text-muted-foreground py-6">
              No renewals scheduled this month.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
